/**
 * @file: The options that FullCalendar gets for the grid.
 *
 * The options change for reasons of their own. The life cycle of the
 * calendar manager does not change them, so they are not in that class. The
 * text inside a bar is in bar-content.js. The marks on a bar are in
 * bar-marks.js.
 *
 * The function gets the manager and keeps no state. The handlers use the
 * manager, so the result is not pure data. The function changes no DOM and
 * starts nothing, so a spec can call it.
 */

import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import enGbLocale from '@fullcalendar/core/locales/en-gb'

import { RANGE_DASH } from 'common/date-time'
import { barContent } from './bar-content'
import { barClasses, inkHatch, isNeverOnScreen, markCoveredStretches } from './bar-marks'
import { markIntervalId } from './calendar-event-element'
import { SNAP_MINUTES } from './grid-geometry'
import { initialViewFor, weekViewOptionsFor } from './narrow-screen'

/**
 * These options give the 24-hour clock with two digits. The running order of
 * the festival uses the same clock. The FullCalendar default wrote "8:45" on a
 * bar and "13" on the axis. Thus "12:30 - 13" read as the end 13:00.
 */
const CLOCK_24H_OPTIONS = Object.freeze({ hour: '2-digit', minute: '2-digit', hour12: false })

/**
 * Read the toolbar state again when the arrow of the now line comes or goes.
 *
 * FullCalendar draws the arrow on the time axis only while today is in the
 * range of the view. The toolbar uses the same test for the Today button. At
 * midnight, the day changes but datesSet does not occur. The arrow then comes
 * or goes, so these hooks tell the toolbar about the new day. The line in a
 * day column also moves between columns, so the code ignores it.
 */
const followTodayOnAxis = (manager, { isAxis, view }) => {
  if (isAxis) manager.followView(view)
}

/**
 * Give the options for the FullCalendar instance.
 *
 * A spec reads the result, so the specs test the slot options and each
 * handler without a calendar.
 *
 * @param {Object} manager - The CalendarManager
 * @param {boolean} [narrow] - Whether the page has the narrow layout
 * @returns {Object} The options FullCalendar is constructed with
 */
export function buildCalendarOptions (manager, narrow = false) {
  return {
    plugins: [timeGridPlugin, interactionPlugin],

    // Overlapping bars are side by side, and no bar covers another. The
    // ribbon shows which one wins the shared stretch. The grid must show only
    // that both are there. 'timeGrid' applies to the week and to the day.
    views: {
      timeGrid: { slotEventOverlap: false },
      timeGridWeek: weekViewOptionsFor(narrow)
    },

    initialView: initialViewFor(narrow),
    // The template of the page draws the toolbar, with Angular buttons. The
    // toolbar of FullCalendar needed a digest in each button, a timer and
    // innerHTML for the title, and a patch for the aria-labels.
    headerToolbar: false,

    // A click on a day header opens that day. The LESS file removes the
    // look of a link, because the main job of the header is to name the day.
    navLinks: true,

    // Give the locale object, not a code string. FullCalendar changes to
    // English with no warning if the code is not registered. en-gb is used
    // instead of the built-in en. It starts the week on Monday and uses a
    // 24-hour clock. The running order of the festival uses the same clock.
    locale: enGbLocale,
    firstDay: 1, // Monday

    // The bars and the axis use one clock. The dash is the dash of
    // the tooltip, so the two texts use the same characters.
    eventTimeFormat: CLOCK_24H_OPTIONS,
    slotLabelFormat: CLOCK_24H_OPTIONS,
    defaultRangeSeparator: RANGE_DASH,

    slotMinTime: '00:00:00',
    slotMaxTime: '24:00:00',
    ...manager.slotZoom.slotOptions(),

    // The value is written here, although the default of FullCalendar is the
    // same 15. It keeps a short bar large enough to click. MIN_VISIBLE_RUN_PX
    // keeps a run on the ribbon visible. These are two different rules, so
    // the two numbers are different.
    eventMinHeight: 15,
    snapDuration: { minutes: SNAP_MINUTES },

    // A red line across the calendar shows the current time.
    nowIndicator: true,
    nowIndicatorDidMount: (arg) => followTodayOnAxis(manager, arg),
    nowIndicatorWillUnmount: (arg) => followTodayOnAxis(manager, arg),

    allDaySlot: false,

    scrollTime: manager.calculateScrollTime(),
    editable: true,
    selectable: true,
    selectMirror: true,
    // The block a drag leaves belongs to the panel that describes it, and the
    // panel is outside the calendar element. FullCalendar clears a selection on
    // any click outside itself, so picking a playlist made the block vanish.
    // clearTimeSelection is the one thing that removes it now.
    unselectAuto: false,
    eventResizableFromStart: true,
    dragRevertDuration: 300,
    eventDurationEditable: true,

    // On hover, the cursor shows that the bar can be dragged. The tooltip
    // comes from eventContent, which puts describeEvent on the inner div.
    eventMouseEnter: (info) => {
      info.el.style.cursor = 'move'
    },

    eventClick: (info) => manager.handleEventClick(info),
    select: (info) => manager.handleTimeSelection(info),
    eventDrop: (info) => manager.timeGestures.handleEventDrop(info),
    eventResize: (info) => manager.timeGestures.handleEventResize(info),

    eventContent: (arg) => barContent(arg, {
      slotMinutes: manager.slotZoom.currentSlotMinutes(),
      neverOnScreen: isNeverOnScreen(manager.week(), arg.event)
    }),

    // New dates replace the day columns, and the lanes of the ribbon go with
    // them. The element keeps its size, so the resize observer does not see
    // this change.
    datesSet: (info) => {
      manager.followView(info.view)
      manager.redrawAfterRender()
    },

    eventOverlap: (stillEvent, movingEvent) => manager.timeGestures.mayOverlap(stillEvent, movingEvent),

    // A gesture starts with no obstacle, and it records the fetch generation
    // that its drop is checked against.
    eventDragStart: () => manager.timeGestures.startGesture(),
    eventResizeStart: () => manager.timeGestures.startGesture(),

    // FullCalendar calls eventAllow only for a position that passed
    // eventOverlap. Such a position has no obstacle. The drag and the resize
    // both call it.
    eventAllow: (dropInfo, draggedEvent) => manager.timeGestures.allowPosition(draggedEvent),

    // The only built-in feedback of FullCalendar is a cursor. That is not
    // sufficient for this rule, so a refused gesture also gets a message. The
    // stop hooks occur at the end of each gesture, refused or not.
    eventDragStop: () => manager.timeGestures.explainRefusedGesture('moved'),
    eventResizeStop: () => manager.timeGestures.explainRefusedGesture('resized'),

    // Each bar gets the stripe colour of its hatch. The stretches of a bar
    // that a higher band covers get the diagonals. The interval id on the
    // element lets the outline and the right-click menu find the interval.
    eventDidMount: (arg) => {
      markIntervalId(arg)
      inkHatch(arg)
      markCoveredStretches(manager.week().intervals, arg)
    },

    // A bar that never reaches the screen gets a mark. The snapshot of the
    // week holds the set of ids, so this call is cheap at every render.
    eventClassNames: (arg) => barClasses(manager, arg),

    // datesSet occurs only at navigation. A create, an edit, a delete and a
    // real-time update change the events, but not the dates. Without this
    // hook the on-screen surfaces and the panel keep the old data.
    eventsSet: () => {
      manager.controller._$timeout(() => {
        manager.renderOnScreenSurfaces()
        manager.controller.followCalendarEvents()
      }, 0)
    },

    // The container has a definite height, and the grid fills it. The
    // previous '96vh' was higher than the 89vh container and cut the bottom.
    height: '100%',
    // The resize observer of the manager measures again after every size
    // change of the element, and a window resize is one of them.
    handleWindowResize: false,

    eventSources: [manager.eventFeed.eventSource()]
  }
}
